<template>
  <v-sheet v-for="(item, index) in items" :key="index">
    <v-row dense>
      <v-col cols="5">
        <v-text-field
          v-model="item.key"
          :label="$t('labels.key')"
          variant="outlined"
          density="compact"
          :rules="getKeyRules(item)"
          @focus="item.touchedKey = true"
          @dragover.prevent
          @drop="onDrop($event, item, 'key')"
        ></v-text-field>
      </v-col>
      <v-col cols="5">
        <v-text-field
          v-model="item.value"
          :label="$t('labels.value')"
          variant="outlined"
          density="compact"
          :rules="getValueRules(item)"
          @focus="item.touchedValue = true"
          @dragover.prevent
          @drop="onDrop($event, item, 'value')"
        ></v-text-field>
      </v-col>
      <v-col cols="2" text-center>
        <v-btn
          v-if="index < items.length - 1"
          class="mt-n1"
          variant="plain"
          icon="mdi-delete"
          color="secondary"
          @click="removeItem(index)"
        ></v-btn>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script>
import { inject, toRefs, reactive, watch } from "vue";
import { drop, checkDrop } from "@/mixins/common-utils.js";
import { useI18n } from "vue-i18n";

export default {
  props: ["dynamicParams", "data"],

  setup() {
    const { t } = useI18n();
    const formData = inject("formData");

    // Initialize parameters if they don't exist
    if (Object.keys(formData).length <= 1) {
      formData.parameters = [
        { key: "", value: "", touchedKey: false, touchedValue: false },
      ];
    }

    // Reactive state for the component
    const state = reactive({
      items:
        formData.parameters.length > 0
          ? revertToItems(formData.parameters)
          : [{ key: "", value: "", touchedKey: false, touchedValue: false }],
    });
    const { items } = toRefs(state);

    // Watch for changes in items and add a new empty row if needed
    watch(
      items,
      (newItems) => {
        const lastItem = newItems[newItems.length - 1];
        if (lastItem.key && lastItem.value) {
          newItems.push({
            key: "",
            value: "",
            touchedKey: false,
            touchedValue: false,
          });
        }
        const existingParams = formData.parameters.filter(
          (param) =>
            !param.name.startsWith("__key__") &&
            !param.name.startsWith("__item_"),
        );
        formData.parameters = [
          ...existingParams,
          ...breakAndEnrichItem(newItems),
        ];
        resetTouchedState(newItems);
      },
      { deep: true },
    );

    // Reset touched state if there is only one empty row
    const resetTouchedState = (items) => {
      if (items.length === 1 && !items[0].key && !items[0].value) {
        items[0].touchedKey = false;
        items[0].touchedValue = false;
      }
    };

    // Computed properties for validation rules
    const getKeyRules = (item) => [
      (v) => !item.touchedKey || !!v || t("strings.key-is-required"),
      (v) =>
        !item.touchedKey ||
        (v && v != "not valid") ||
        t("strings.key-must-be-string"),
    ];

    const getValueRules = (item) => [
      (v) => !item.touchedValue || !!v || t("strings.value-is-required"),
    ];

    // Handle drop event for drag-and-drop
    const onDrop = (event, item, targetName) => {
      event.stopPropagation();
      try {
        const isKey = targetName === "key";
        const isValidDrop = isKey ? checkDrop(event) === "text" : true;

        if (isValidDrop) {
          drop(event, targetName, item);
        } else {
          item[targetName] = t("strings.not-valid");
        }
      } catch (error) {
        console.error("Error handling drop event: ", error);
      }
    };

    // Remove a key/value pair
    const removeItem = (index) => {
      try {
        items.value.splice(index, 1);
        formData.parameters = breakAndEnrichItem(items.value);
        resetTouchedState(items.value);
      } catch (error) {
        console.error("Error removing item: ", error);
      }
    };

    // Split an array of objects with key-value pairs
    // into an array of object pairs with a single property key or value
    function breakAndEnrichItem(items) {
      const createObject = (name, property) => ({
        name,
        property,
        property2: "undefined",
        operator: "undefined",
        sourceexp: true,
        bind: true,
        custom: true,
      });
      return items.flatMap((item, index) =>
        item.key && item.value
          ? [
              createObject("__key__" + index, item.key),
              createObject("__item_" + index, item.value),
            ]
          : [],
      );
    }

    // Transforms an array of objects with a single property into an array of objects with key-value pairs
    function revertToItems(parameters) {
      let items = [];
      let currentItem = {};
      parameters.forEach((item) => {
        if (item.name.startsWith("__key__")) {
          if (currentItem.key !== undefined) {
            items.push(currentItem);
          }
          currentItem = {
            key: item.property,
            value: "",
            touchedKey: false,
            touchedValue: false,
          };
        } else if (item.name.startsWith("__item_")) {
          currentItem.value = item.property;
        }
      });
      if (currentItem.key !== undefined) {
        items.push(currentItem);
      }
      // Add an empty key/value pair at the end
      items.push({
        key: "",
        value: "",
        touchedKey: false,
        touchedValue: false,
      });
      return items;
    }

    return {
      onDrop,
      formData,
      items,
      removeItem,
      getKeyRules,
      getValueRules,
    };
  },
};
</script>
